import { AlertTriangle, Brain, CalendarClock, Cpu, FileText, FlaskConical, GitBranch, GitPullRequest, Headphones, HelpCircle, Home, LifeBuoy, MessageSquare, Monitor, Moon, Network, Plug, Settings, Shield, Sparkles, Sun, Zap } from "lucide-react"
import React from "react"
import { useLocation } from "react-router"
import { cn } from "src/common/utils"
import { Link } from "src/components/ui/link"
import { Tooltip } from "src/components/ui/tooltip"
import { useMe, usePermissions } from "src/context/MeContext"
import { useNavigation } from "src/context/NavigationContext"
import { useTheme } from "src/context/ThemeContext"
import { getKnowledgeBasePath, getProductName, isRootlyBranded, isSlackManagedByRootly } from "src/utils/brandTheme"

interface NavigationItem {
  to: string
  label: string
  icon: React.ReactNode
  showNotificationDot?: boolean
  requiredScope?: string  // Scope required to view this nav item
  requiresSuperAdmin?: boolean  // Only show to superadmin users
  requiresFeatureFlag?: string  // Show if org has this flag enabled OR user is superadmin
  alsoActiveFor?: string[]  // Additional paths that should highlight this nav item
}

interface NavigationGroup {
  header?: string  // Optional header label (e.g., "AUTOMATE")
  items: NavigationItem[]
}

const baseNavigationGroups: NavigationGroup[] = [
  {
    // Home - no header
    items: [
      { to: "/", label: "Home", icon: <Home className="h-4 w-4" /> },
    ]
  },
  {
    header: "AUTOMATE",
    items: [
      { to: "/chat", label: "Chat", icon: <MessageSquare className="h-4 w-4" /> },
      { to: "/alerts", label: "Alert", icon: <AlertTriangle className="h-4 w-4" />, requiredScope: "read:alert_responders", alsoActiveFor: ["/investigations"] },
      { to: "/incidents", label: "Incident", icon: <Shield className="h-4 w-4" />, requiredScope: "read:incidents" },
      { to: "/support/agents", label: "Support", icon: <Headphones className="h-4 w-4" />, requiredScope: "read:support_agents" },
      { to: "/digests", label: "Digest", icon: <CalendarClock className="h-4 w-4" />, requiredScope: "read:digests" },
      { to: "/oncall", label: "Oncall", icon: <LifeBuoy className="h-4 w-4" />, requiresSuperAdmin: true },
      { to: "/cicd", label: "CI/CD", icon: <GitBranch className="h-4 w-4" />, requiresSuperAdmin: true },
      { to: "/autopilot", label: "Autopilot", icon: <Cpu className="h-4 w-4" />, requiredScope: "read:optimizers", requiresFeatureFlag: "AUTOPILOT" },
    ]
  },
  {
    header: "TUNE",
    items: [
      // eslint-disable-next-line custom/no-hardcoded-brand-name -- hardcoded URL
      { to: "/knowledge/tierzero.md", label: "Instructions", icon: <FileText className="h-4 w-4" />, requiredScope: "read:knowledge" },
      { to: "/skills", label: "Skills", icon: <Sparkles className="h-4 w-4" />, requiredScope: "read:skills" },
      { to: "/context", label: "Context Engine", icon: <Brain className="h-4 w-4" />, requiredScope: "read:knowledge" },
      { to: "/service-intelligence", label: "Service Intelligence", icon: <Network className="h-4 w-4" />, requiresSuperAdmin: true },
      { to: "/capabilities", label: "Capabilities", icon: <Zap className="h-4 w-4" />, requiredScope: "read:capabilities" },
      { to: "/studio?fromSidebar=true", label: "Prompt Studio", icon: <FlaskConical className="h-4 w-4" />, requiredScope: "read:prompt_studio" },
    ]
  },
  {
    header: "MANAGE",
    items: [
      { to: "/pull-requests", label: "Pull Requests", icon: <GitPullRequest className="h-4 w-4" />, requiredScope: "read:pull_requests" },
      { to: "/mcp", label: "MCP", icon: <Plug className="h-4 w-4" /> },
      { to: "/settings", label: "Settings", icon: <Settings className="h-4 w-4" /> },  // Settings shown if user can access any settings page
    ]
  },
]

interface LeftNavigationProps {
  className?: string
  isCollapsed?: boolean
}

export function LeftNavigation({ className, isCollapsed = false }: LeftNavigationProps) {
  const location = useLocation()
  const { hasScope, canRead, isSuperAdmin } = usePermissions()
  const me = useMe()
  const { isMobile, isLeftNavOpen, setLeftNavOpen } = useNavigation()
  const { theme, setTheme } = useTheme()

  const brandTheme = me.organization?.brandTheme ?? "TIERZERO"
  // ROOTLY and ROOTLY_COBRAND orgs should not see the User Guide
  const hideUserGuide = isSlackManagedByRootly(brandTheme)

  const isActiveRoute = (item: NavigationItem) => {
    // Remove query params from path for comparison
    const cleanPath = item.to.split("?")[0]
    const pathname = location.pathname

    // Root path "/" is exact-match-only — every other pathname startsWith("/")
    if (cleanPath === "/") {
      return pathname === "/"
    }

    // Exact match or starts with path followed by /
    if (pathname === cleanPath || pathname.startsWith(`${cleanPath}/`)) {
      return true
    }

    // Check additional paths that should highlight this nav item
    if (item.alsoActiveFor) {
      for (const additionalPath of item.alsoActiveFor) {
        if (pathname === additionalPath || pathname.startsWith(`${additionalPath}/`)) {
          return true
        }
      }
    }

    return false
  }

  // Check if user can access any settings page
  const canAccessSettings = React.useMemo(() => {
    return (
      canRead("integrations") ||
      canRead("teams") ||
      canRead("billing") ||
      canRead("feature_enablement")
    )
  }, [canRead])

  // Build navigation groups dynamically based on user role
  const navigationGroups = React.useMemo(() => {
    const brandTheme = me.organization?.brandTheme ?? "TIERZERO"

    // ROOTLY and ROOTLY_COBRAND get Rootly-specific UI changes (not ROOTLY_HYBRID)
    const isPureRootly = brandTheme === "ROOTLY" || brandTheme === "ROOTLY_COBRAND"
    const knowledgeBasePath = getKnowledgeBasePath(brandTheme)
    // Incident response disabled for Rootly-branded orgs and explicitly disabled orgs
    const INCIDENT_RESPONSE_DISABLED_ORG_IDS = new Set([
      "R3JhcGhRTE9yZ2FuaXphdGlvbjo2", // 6
    ])
    const isIncidentResponseEnabled = !isPureRootly && !INCIDENT_RESPONSE_DISABLED_ORG_IDS.has(me.organization?.id ?? "")
    const enabledFlags = new Set(me.organization?.enabledFeatureFlags ?? [])
    const filteredGroups = baseNavigationGroups.map((group) => {
      const filteredItems = group.items.filter((item) => {
        // Check superadmin requirement first
        if (item.requiresSuperAdmin && !isSuperAdmin) {
          return false
        }

        // Feature-flag gated items: superadmin bypass, otherwise require the flag
        if (item.requiresFeatureFlag && !isSuperAdmin && !enabledFlags.has(item.requiresFeatureFlag)) {
          return false
        }

        if (isPureRootly && (item.to === "/digests" || item.to === "/support/agents" || item.to === "/incidents")) {
          return false
        }

        // MCP setup page is hidden for Rootly-branded orgs (white-label and co-brand)
        if (item.to === "/mcp" && isRootlyBranded(brandTheme)) {
          return false
        }

        // Incident response is gated by org-level allowlist
        if (item.to === "/incidents" && !isIncidentResponseEnabled) {
          return false
        }

        // If no scope required, show to everyone
        if (!item.requiredScope) {
          // Special case: Settings only shown if user can access any settings page
          if (item.to === "/settings") {
            return canAccessSettings
          }
          return true
        }
        // Check if user has required scope
        return hasScope(item.requiredScope as any)
      }).map((item) => {
        // Update path based on brand theme (label is always "Instructions")
        // eslint-disable-next-line custom/no-hardcoded-brand-name -- hardcoded URL
        if (item.to === "/knowledge/tierzero.md") {
          return { ...item, to: knowledgeBasePath }
        }
        if (item.to === "/mcp") {
          return { ...item, label: `${getProductName(brandTheme)} MCP` }
        }
        return item
      })

      return {
        ...group,
        items: filteredItems,
      }
    }).filter((group) => group.items.length > 0) // Remove empty groups

    return filteredGroups
  }, [hasScope, canRead, canAccessSettings, isSuperAdmin, me])

  const handleNavClick = () => {
    if (isMobile) {
      setLeftNavOpen(false)
    }
  }

  const navContent = (
    <div
      className={cn(
        "flex flex-col bg-background border-r border-border relative h-full flex-shrink-0",
        isMobile ? "w-64" : isCollapsed ? "w-14" : "w-48",
        !isMobile && "transition-all duration-200 ease-in-out",
        className
      )}
    >

      {/* Navigation items */}
      <nav className="flex-1 pt-2">
        <div className="px-2">
          {navigationGroups.map((group, groupIndex) => (
            <div key={group.header || `group-${groupIndex}`} className={cn(groupIndex > 0 && "mt-3")}>
              {/* Group divider */}
              {group.header && groupIndex > 0 && (
                <div className="mb-2 mx-3 h-px bg-border" />
              )}

              {/* Group items */}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  // TODO: Re-enable onboarding gate once team instruction generation is complete
                  // const isOnboardingItem = item.to === "/onboarding"
                  // const isDisabled = !isSetupTeamCompleted && !isOnboardingItem
                  const isDisabled = false

                  const isActive = isActiveRoute(item)
                  const linkElement = (
                    <Link
                      key={item.to}
                      to={isDisabled ? "#" : item.to}
                      noUnderline
                      onClick={(e) => {
                        if (isDisabled) {
                          e.preventDefault()
                          return
                        }
                        handleNavClick()
                      }}
                      className={cn(
                        "flex items-center rounded-md font-medium transition-colors duration-150 group relative h-8 px-3 text-[13px] leading-tight focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                        isDisabled
                          ? "text-muted-foreground"
                          : isActive
                            ? "text-foreground bg-accent"
                            : "text-secondary-foreground hover:bg-accent hover:text-foreground"
                      )}
                    >
                      <span className={cn("relative flex-shrink-0 transition-[margin] duration-200", isCollapsed && !isMobile ? "" : "mr-3")}>
                        {item.icon}
                        {/* Notification dot */}
                        {item.showNotificationDot && (
                          <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                        )}
                      </span>
                      <span className={cn(
                        "relative whitespace-nowrap transition-opacity duration-200 overflow-hidden",
                        isCollapsed && !isMobile ? "opacity-0 w-0" : "opacity-100"
                      )}>
                        {item.label}
                      </span>

                      {/* Tooltip for collapsed state (only when not disabled) */}
                      {isCollapsed && !isMobile && !isDisabled && (
                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-card text-foreground text-xs rounded shadow-sm border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                          {item.label}
                        </div>
                      )}
                    </Link>
                  )

                  return isDisabled ? (
                    <Tooltip
                      key={item.to}
                      content="Please complete onboarding for this team to get started."
                      position="right"
                      maxWidth="12rem"
                    >
                      {linkElement}
                    </Tooltip>
                  ) : (
                    <React.Fragment key={item.to}>{linkElement}</React.Fragment>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Theme toggle and help link at bottom */}
      <div className="pb-2 px-2">
        {/* Theme toggle - only visible to super admins until light mode is polished */}
        {isSuperAdmin && (
          <ThemeSegmentedControl
            theme={theme}
            setTheme={setTheme}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
          />
        )}

      {!hideUserGuide && (
        <div>
          <a
            // eslint-disable-next-line custom/no-hardcoded-brand-name -- hardcoded URL
            href="https://docs.tierzero.ai/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleNavClick}
            className={cn(
              "flex items-center rounded-md font-medium transition-colors duration-150 group relative h-8 px-3 text-[13px] leading-tight",
              "text-secondary-foreground hover:bg-accent hover:text-foreground"
            )}
          >
            <span className={cn("flex-shrink-0 relative transition-[margin] duration-200", isCollapsed && !isMobile ? "" : "mr-3")}>
              <HelpCircle className="h-4 w-4" />
            </span>
            <span className={cn(
              "whitespace-nowrap transition-opacity duration-200 overflow-hidden",
              isCollapsed && !isMobile ? "opacity-0 w-0" : "opacity-100"
            )}>
              User Guide
            </span>

            {/* Tooltip for collapsed state */}
            {isCollapsed && !isMobile && (
              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-card text-foreground text-xs rounded shadow-sm border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                User Guide
              </div>
            )}
          </a>
        </div>
      )}
      </div>
    </div>
  )

  // Mobile: render as overlay drawer
  if (isMobile) {
    return (
      <div
        className={cn(
          "fixed inset-0 z-50 transition-opacity duration-200",
          isLeftNavOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setLeftNavOpen(false)}
        />
        {/* Drawer */}
        <div
          className={cn(
            "absolute left-0 top-0 h-full transform transition-transform duration-200",
            isLeftNavOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          {navContent}
        </div>
      </div>
    )
  }

  // Desktop: render inline
  return navContent
}

type ThemeOption = "light" | "dark" | "system"

const THEME_OPTIONS: { value: ThemeOption; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "light", label: "Light Mode", icon: Sun },
  { value: "dark", label: "Dark Mode", icon: Moon },
  { value: "system", label: "System Mode", icon: Monitor },
]

interface ThemeSegmentedControlProps {
  theme: ThemeOption
  setTheme: (theme: ThemeOption) => void
  isCollapsed: boolean
  isMobile: boolean
}

const ThemeSegmentedControl = React.memo<ThemeSegmentedControlProps>(function ThemeSegmentedControl({
  theme,
  setTheme,
  isCollapsed,
  isMobile,
}) {
  const currentIndex = THEME_OPTIONS.findIndex(o => o.value === theme)
  const current = THEME_OPTIONS[currentIndex] ?? THEME_OPTIONS[0]
  const next = THEME_OPTIONS[(currentIndex + 1) % THEME_OPTIONS.length]
  const CurrentIcon = current.icon
  const collapsed = isCollapsed && !isMobile

  return (
    <button
      onClick={() => setTheme(next.value)}
      className={cn(
        "flex items-center rounded-md font-medium transition-colors duration-150 group relative h-8 w-full px-3 text-[13px] leading-tight",
        "text-secondary-foreground hover:bg-accent hover:text-foreground"
      )}
      aria-label={current.label}
      title={`${current.label} — click to switch to ${next.label}`}
    >
      <span className={cn("flex-shrink-0 relative transition-[margin] duration-200", collapsed ? "" : "mr-3")}>
        <CurrentIcon className="h-4 w-4" />
      </span>
      <span className={cn(
        "whitespace-nowrap transition-opacity duration-200 overflow-hidden",
        collapsed ? "opacity-0 w-0" : "opacity-100"
      )}>
        {current.label}
      </span>
      {collapsed && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-card text-foreground text-xs rounded shadow-sm border border-border opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
          {current.label}
        </div>
      )}
    </button>
  )
})
